import { isNullOrWhitespace } from '@/utils/stringHelper';
import { expandNamesByLanguage, UserLanguage } from '@/utils/translation';
import { Currency, DEFAULT_CURRENCY } from '@/utils/price';

import type { GetAllContainerTypesAdminQuery } from '../../../graphql/queries/getAllContainerTypesAdmin.generated';
import type { AdminContainerTypeInput } from '../../../graphql/generatedTypes';
import type { ContainerType } from '../../ContainerComponents/ContainerPage';
import type {
  ContainerOverduePrice,
  StoreDefaultContainerPrice,
} from '../ContainerPriceTable';
import type { NewContainerPrice } from '../newContainerType';

/**
 * Mapping between the colugo AdminContainerType wire format and the frontend
 * `ContainerType` model, plus the pricing payload helpers shared by
 * useContainerTypesAdmin.
 */

export const DRAFT_ID = -1;

/** A not-yet-created container type edited in the same sheet as real ones. */
export type ContainerTypeDraft = ContainerType & {
  pricesByCurrency: { [currency: string]: NewContainerPrice };
};

export type OverduePricesByCurrency = {
  [currency: string]: { [containerTypeId: number]: ContainerOverduePrice };
};

export const deepClone = <T>(value: T): T => JSON.parse(JSON.stringify(value));

export const toInt = (value: unknown): number => {
  const parsed = parseInt(String(value ?? '0'), 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/** A single container-type row as returned by getAllContainerTypesAdmin. */
export type AdminContainerTypeFields =
  GetAllContainerTypesAdminQuery['getAllContainerTypesAdmin']['items'][number];

export function fromGql(gqlCt: AdminContainerTypeFields): ContainerType {
  let namesByLanguage: { [lang: string]: string } = {};
  try {
    namesByLanguage = JSON.parse(gqlCt.names_by_language_string) ?? {};
  } catch {
    /* tolerate malformed names */
  }
  return expandNamesByLanguage({
    id: gqlCt.id,
    name: gqlCt.name ?? '',
    internalName: gqlCt.name ?? '',
    namesByLanguage,
    class: gqlCt.class,
    orderKey: gqlCt.order_key,
    iconUrl: gqlCt.icon_url ?? '',
    sizeHelper: gqlCt.size_helper ?? '',
    imageUrl: gqlCt.image_url ?? '',
    countryCodes: gqlCt.country_codes ?? [],
    hexCode: gqlCt.hex_code ?? '',
    packingUnit: gqlCt.packing_unit,
    groupId: gqlCt.group_id ?? 0,
    sku: gqlCt.sku ?? '',
    branding: gqlCt.branding ?? '',
    legalOwner: gqlCt.legal_owner ?? '',
    packingInstructions: gqlCt.packing_instructions ?? '',
    weight: gqlCt.weight,
    archived: gqlCt.archived,
    segment: gqlCt.segment,
    containerEquivalentTypeIds: gqlCt.container_equivalent_type_ids ?? [],
  } as any) as ContainerType;
}

export function toInput(ct: ContainerType): AdminContainerTypeInput {
  const namesByLanguage: { [lang: string]: string } = {};
  for (const lang of Object.keys(UserLanguage)) {
    if (ct.namesByLanguage?.[lang] != null) {
      namesByLanguage[lang] = ct.namesByLanguage[lang];
    }
  }
  return {
    id: ct.id,
    names_by_language_string: JSON.stringify(namesByLanguage),
    class: ct.class,
    // Sent as a Float: an integer for untouched rows, or the fractional
    // "desired position" the reorder stepper produced. The server flattens
    // the whole set to dense integers on save (reindexOrderKeys).
    order_key: ct.orderKey,
    icon_url: ct.iconUrl || null,
    size_helper: ct.sizeHelper || null,
    image_url: ct.imageUrl || null,
    country_codes: ct.countryCodes ?? [],
    hex_code: ct.hexCode || null,
    packing_unit: toInt(ct.packingUnit),
    group_id: toInt(ct.groupId),
    sku: isNullOrWhitespace(ct.sku) ? null : ct.sku.trim(),
    branding: ct.branding || null,
    legal_owner: ct.legalOwner || null,
    packing_instructions: ct.packingInstructions || null,
    weight: toInt(ct.weight),
    archived: !!ct.archived,
    segment: ct.segment || null,
    // Always sent as an array (never null) — the server treats null as "leave the
    // group untouched" and [] as "take this type out of its group", and this form
    // always knows the intended membership. Self is stripped defensively; the
    // server ignores it too.
    container_equivalent_type_ids: (ct.containerEquivalentTypeIds ?? []).filter(
      (id) => id !== ct.id,
    ),
  };
}

/** Fill every supported currency of a store-defaults row from EUR / zeros. */
export function expandPrice(price: StoreDefaultContainerPrice) {
  const defaultPrice = {
    TransactionPrice: '0',
    StoreLostCost: '0',
    StoreLostCostUpper: '0',
    StoreLostCostLower: '0',
    RentalPrice: '0',
    CleaningPrice: '0',
    LostPrice: '0',
    DepositPrice: '0',
    CostPrice: '0',
    LogisticPartnerCleaningPrice: '0',
  };
  price[DEFAULT_CURRENCY] = {
    ...defaultPrice,
    ...(price[DEFAULT_CURRENCY] || {}),
  };
  for (const c of Object.keys(Currency)) {
    price[c] = { ...price[DEFAULT_CURRENCY], ...(price[c] || {}) };
  }
  return price;
}

/** Parse a store-defaults row into the numeric shape the config JSON stores. */
export function normalizeStoreRow(info: StoreDefaultContainerPrice) {
  const next: any = { Id: info.Id };
  for (const key of Object.keys(info)) {
    if (key === 'Id') continue;
    const p: any = (info as any)[key] ?? {};
    next[key] = {
      TransactionPrice: parseFloat(p.TransactionPrice || '0') || 0,
      StoreLostCost: toInt(p.StoreLostCost),
      StoreLostCostUpper: toInt(p.StoreLostCostUpper),
      StoreLostCostLower: toInt(p.StoreLostCostLower),
      RentalPrice: toInt(p.RentalPrice),
      CleaningPrice: toInt(p.CleaningPrice),
      LostPrice: toInt(p.LostPrice),
      DepositPrice: toInt(p.DepositPrice),
      CostPrice: toInt(p.CostPrice),
      LogisticPartnerCleaningPrice: toInt(p.LogisticPartnerCleaningPrice),
    };
  }
  return next;
}
